import { formatCount, formatPercent, rateToInput } from '@/lib/format';
import type { DisplayNodes, FunnelResult, ScreeningOutcomes } from '@/model/types';
import { useFunnelStore } from '@/store/useFunnelStore';
import { NumberField } from './NumberField';
import { Note, ProvenanceTag, Readout, Section } from './parts';

interface Props {
  result: FunnelResult;
  display: DisplayNodes;
  onEditSources: () => void;
}

interface Row {
  key: keyof ScreeningOutcomes;
  label: string;
  note: string;
}

/** Level one: the PHQ-9 threshold. These two partition everyone screened. */
const THRESHOLD_ROWS: Row[] = [
  {
    key: 'noFollowup',
    label: 'PHQ-9 under 10',
    note: 'No follow-up owed. The measure is closed for these members.',
  },
  {
    key: 'followupRequired',
    label: 'PHQ-9 10 or higher',
    note: 'The only branch that owes a 30-day follow-up.',
  },
];

export function ScreenStage({ result, display, onEditSources }: Props) {
  const setOutcome = useFunnelStore((s) => s.setOutcome);
  const outcomes = result.params.outcomes;

  const rawTotal = outcomes.noFollowup + outcomes.followupRequired;
  const offBy = Math.abs(rawTotal - 1) > 0.0005;
  const screened = Math.max(1, result.nodes.screened);

  return (
    <>
      <Note>
        The screening rate itself is derived: {formatPercent(result.rates.screenGivenReached)} of
        those reached, blended from the per-source screen rates in{' '}
        <button type="button" className="link-button" onClick={onEditSources}>
          Population
        </button>
        . What follows is what happens to a screen once it is completed.
      </Note>

      <Section title="PHQ-9 threshold · share of screened">
        <div className="assumption-grid">
          {THRESHOLD_ROWS.map((row) => (
            <div key={row.key} className="assumption-row">
              <div className="assumption-copy">
                <span className="assumption-label">{row.label}</span>
                <small>{row.note}</small>
                <ProvenanceTag kind="assumption" />
              </div>
              <NumberField
                label={row.label}
                hideLabel
                value={rateToInput(outcomes[row.key])}
                max={100}
                step={0.5}
                suffix="%"
                onCommit={(pct) => setOutcome(row.key, pct / 100)}
              />
            </div>
          ))}
        </div>
        <p className={`share-total${offBy ? ' is-warn' : ''}`}>
          Shares total {formatPercent(rawTotal)}
          {offBy ? ' — normalized to 100% when drawn.' : '.'}
        </p>
      </Section>

      <Section title="Within the no-follow-up group">
        <div className="assumption-grid">
          <div className="assumption-row">
            <div className="assumption-copy">
              <span className="assumption-label">PHQ-2 under 3</span>
              <small>
                Screened negative on the PHQ-2, so no PHQ-9 was administered. The rest scored
                positive, completed a PHQ-9, and came in under 10.
              </small>
              <ProvenanceTag kind="assumption" />
            </div>
            <NumberField
              label="PHQ-2 under 3"
              hideLabel
              value={rateToInput(outcomes.phq2NegativeShare)}
              max={100}
              step={0.5}
              suffix="%"
              onCommit={(pct) => setOutcome('phq2NegativeShare', pct / 100)}
            />
          </div>
        </div>
      </Section>

      <Section title="Within the follow-up group">
        <div className="assumption-grid">
          <div className="assumption-row">
            <div className="assumption-copy">
              <span className="assumption-label">C-SSRS required</span>
              <small>
                Suicide-risk follow-up on top of the measure work. These members stay in the chase
                pool and still owe their 30-day follow-up.
              </small>
              <ProvenanceTag kind="assumption" />
            </div>
            <NumberField
              label="C-SSRS required"
              hideLabel
              value={rateToInput(outcomes.cssrsShare)}
              max={100}
              step={0.5}
              suffix="%"
              onCommit={(pct) => setOutcome('cssrsShare', pct / 100)}
            />
          </div>
        </div>
      </Section>

      <Section title="Resulting split">
        <div className="readout-grid is-tight">
          <Readout
            label="PHQ-2 negative"
            value={formatCount(display.phq2Negative)}
            note={`${formatPercent(result.nodes.phq2Negative / screened)} of screened`}
          />
          <Readout
            label="PHQ-9 under 10"
            value={formatCount(display.phq9UnderTen)}
            note={`${formatPercent(result.nodes.phq9UnderTen / screened)} of screened`}
          />
          <Readout
            label="Follow-up required"
            value={formatCount(display.followupRequired)}
            note={`${formatPercent(result.rates.followupRequiredShare)} of screened`}
          />
          <Readout
            label="Screening rate"
            value={formatPercent(result.rates.screeningRate)}
            note="of total population"
          />
        </div>
      </Section>

      <Note tone="warn">
        <strong>{formatCount(display.cssrsNeeded)}</strong> members need a C-SSRS suicide-risk
        follow-up. That is overlapping clinical work, not a branch of the funnel — it is drawn as a
        dashed connector because those members are still in the chase pool and still owe their
        30-day follow-up.
      </Note>
    </>
  );
}
